import { api } from './axios';

/**
 * Tournaments API — турнирная система
 * @see FRONTEND_DUEL_API.md#6-tournament-api
 */

// ==================== Турниры ====================

/**
 * Получить список турниров с пагинацией
 * @param {Object} params - Параметры запроса
 * @param {number} [params.page=0] - Номер страницы
 * @param {number} [params.size=10] - Размер страницы
 * @param {Array<string>} [params.statuses] - Фильтр по статусам
 * @returns {Promise<Object>} Пагинированный список турниров
 */
export const getTournaments = (params = {}) => {
    const { page = 0, size = 10, statuses } = params;
    return api.get('/tournaments', { 
        params: { 
            page, 
            size,
            ...(statuses && { statuses })
        } 
    }).then((res) => res.data);
};

/**
 * Получить активные турниры (REGISTRATION, UPCOMING, ACTIVE)
 * @param {Object} params - Параметры пагинации
 * @param {number} [params.page=0] - Номер страницы
 * @param {number} [params.size=10] - Размер страницы
 * @returns {Promise<Object>} Пагинированный список активных турниров
 */
export const getActiveTournaments = (params = {}) => {
    const { page = 0, size = 10 } = params;
    return api.get('/tournaments/active', { params: { page, size } }).then((res) => res.data);
};

/**
 * Получить мои турниры
 * @param {Object} params - Параметры пагинации
 * @param {number} [params.page=0] - Номер страницы
 * @param {number} [params.size=10] - Размер страницы
 * @returns {Promise<Object>} Пагинированный список моих турниров
 */
export const getMyTournaments = (params = {}) => {
    const { page = 0, size = 10 } = params;
    return api.get('/tournaments/my', { params: { page, size } }).then((res) => res.data);
};

/**
 * Получить детали турнира
 * @param {string} id - ID турнира
 * @returns {Promise<Object>} Детали турнира
 */
export const getTournamentById = (id) => {
    return api.get(`/tournaments/${id}`).then((res) => res.data);
};

/**
 * Создать турнир
 * @param {Object} data - Данные турнира
 * @param {string} data.title - Название турнира
 * @param {string} data.description - Описание
 * @param {string} [data.coverImageId] - ID обложки
 * @param {string} data.prizePool - Тип призового фонда: CASH, POINTS, MIXED
 * @param {number} [data.prizePoolAmount] - Сумма призового фонда
 * @param {string} [data.currency] - Валюта (RUB, USD, EUR)
 * @param {number} data.maxParticipants - Максимум участников
 * @param {number} [data.minParticipants] - Минимум для начала
 * @param {number} [data.entryFee] - Взнос за участие
 * @param {string} data.format - Формат: SINGLE_ELIMINATION, DOUBLE_ELIMINATION, ROUND_ROBIN, SWISS
 * @param {string} data.gameType - Тип игры: DUEL, BATTLE, FREESTYLE
 * @param {string} data.registrationDeadline - Дедлайн регистрации (ISO 8601)
 * @param {string} data.startDate - Дата начала (ISO 8601)
 * @returns {Promise<Object>} Созданный турнир
 */
export const createTournament = (data) => {
    return api.post('/tournaments', data).then((res) => res.data);
};

/**
 * Обновить турнир
 * @param {string} id - ID турнира
 * @param {Object} data - Данные для обновления
 * @returns {Promise<Object>} Обновлённый турнир
 */
export const updateTournament = (id, data) => {
    return api.put(`/tournaments/${id}`, data).then((res) => res.data);
};

/**
 * Удалить турнир
 * @param {string} id - ID турнира
 * @returns {Promise<void>}
 */
export const deleteTournament = (id) => {
    return api.delete(`/tournaments/${id}`).then((res) => res.data);
};

/**
 * Зарегистрироваться на турнир
 * @param {string} id - ID турнира
 * @param {Object} [data] - Данные регистрации
 * @param {string} [data.teamName] - Название команды (для командных турниров)
 * @returns {Promise<Object>} Данные участника
 */
export const registerForTournament = (id, data = {}) => {
    return api.post(`/tournaments/${id}/register`, data).then((res) => res.data);
};

/**
 * Отменить регистрацию на турнир
 * @param {string} id - ID турнира
 * @returns {Promise<void>}
 */
export const unregisterFromTournament = (id) => {
    return api.post(`/tournaments/${id}/unregister`).then((res) => res.data);
};

/**
 * Начать турнир (только для организатора)
 * @param {string} id - ID турнира
 * @returns {Promise<Object>} Турнир со статусом ACTIVE
 */
export const startTournament = (id) => {
    return api.post(`/tournaments/${id}/start`).then((res) => res.data);
};

/**
 * Завершить турнир (только для организатора)
 * @param {string} id - ID турнира
 * @returns {Promise<Object>} Турнир со статусом FINISHED
 */
export const finishTournament = (id) => {
    return api.post(`/tournaments/${id}/finish`).then((res) => res.data);
};

/**
 * Отменить турнир (только для организатора)
 * @param {string} id - ID турнира
 * @returns {Promise<Object>} Турнир со статусом CANCELLED
 */
export const cancelTournament = (id) => {
    return api.post(`/tournaments/${id}/cancel`).then((res) => res.data);
};

// ==================== Участники ====================

/**
 * Получить участников турнира
 * @param {string} id - ID турнира
 * @returns {Promise<Array>} Список участников
 */
export const getTournamentParticipants = (id) => {
    return api.get(`/tournaments/${id}/participants`).then((res) => res.data);
};

// ==================== Сетка турнира ====================

/**
 * Получить сетку турнира
 * @param {string} id - ID турнира
 * @returns {Promise<Object>} Сетка турнира с этапами и матчами
 */
export const getTournamentBracket = (id) => {
    return api.get(`/tournaments/${id}/bracket`).then((res) => res.data);
};

// ==================== Матчи ====================

/**
 * Установить результат матча (только для организатора)
 * @param {string} matchId - ID матча
 * @param {Object} data - Результат матча
 * @param {string} data.winnerId - ID победителя
 * @param {string} [data.loserId] - ID проигравшего
 * @returns {Promise<Object>} Обновлённый матч
 */
export const setMatchResult = (matchId, data) => {
    return api.put(`/tournaments/matches/${matchId}/result`, data).then((res) => res.data);
};

/**
 * Отменить результат матча (только для организатора)
 * @param {string} matchId - ID матча
 * @returns {Promise<Object>} Обновлённый матч
 */
export const cancelMatchResult = (matchId) => {
    return api.delete(`/tournaments/matches/${matchId}/result`).then((res) => res.data);
};

export default {
    getTournaments,
    getActiveTournaments,
    getMyTournaments,
    getTournamentById,
    createTournament,
    updateTournament,
    deleteTournament,
    registerForTournament,
    unregisterFromTournament,
    startTournament,
    finishTournament,
    cancelTournament,
    getTournamentParticipants,
    getTournamentBracket,
    setMatchResult,
    cancelMatchResult
};
